import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import SwiperCore from 'swiper';
import 'swiper/css';
import '@/assets/styles/components/appointment-modal.styles.scss';
import { CalendarDay } from './appointment-modal.types';
import { CALENDAR_DAYS } from './appointment-modal.constants';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { useInfiniteQueryProvider } from '@/hooks/use-infinity-query-provider';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { AppointmentCreateRequest, AppointmentCreateRequestLiveServiceTypeEnum, ExpertDailySlotResponse, ExpertStudentResponse } from '@dopingtech/live_expert_bff_api_ts';
import { AppointmentSubjectResponse, ExpertDailySlotResponseSlotStatusEnum, ManualCalendarSlotUpdateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advanceFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import { toast } from 'react-toastify';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { useQueryClient } from 'react-query';
import ReactPlaceholder from 'react-placeholder';
import { Select } from '@/components';
import { SelectOption } from '@/components/select/select.types.ts';
import { AppointmentSubjectResponseSubjectTypeEnum } from '@dopingtech/live_expert_bff_api_ts/dist/api';
import { useFeatureFlag } from 'configcat-react';
import clsx from 'clsx';

dayjs.extend(customParseFormat);
dayjs.extend(advanceFormat);
dayjs.extend(timezone);

const AppointmentModal: React.FC<{
	onClose: () => void;
	liveServiceType?: ManualCalendarSlotUpdateRequestLiveServiceTypeEnum;
}> = ({ onClose, liveServiceType = AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching }) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const studentsListRef = useRef<HTMLDivElement>(null);
	const daysGridRef = useRef<HTMLDivElement>(null);
	const [swiper, setSwiper] = useState<SwiperCore | null>(null);
	const [search, setSearch] = useState<string>('');
	const [debouncedSearch, setDebouncedSearch] = useState<string>('');
	const [selectedStudent, setSelectedStudent] = useState<ExpertStudentResponse | null>(null);
	const [selectedLessonSubject, setSelectedLessonSubject] = useState<string>('');
	const [selectedSubject, setSelectedSubject] = useState<string>('');
	const [selectedUnit, setSelectedUnit] = useState<string>('');
	const [selectedTopic, setSelectedTopic] = useState<AppointmentSubjectResponse | null>(null);
	const [selectedDate, setSelectedDate] = useState<string>('');
	const [currentDate, setCurrentDate] = useState(dayjs());
	const [selectedTimeSlot, setSelectedTimeSlot] = useState<ExpertDailySlotResponse | null>(null);

	const queryClient = useQueryClient();
	const { value: maxCoachActiveSlotDays } = useFeatureFlag('maxCoachActiveSlotDays', 28);
	const { value: isActiveRestriction } = useFeatureFlag('isactiverestriction', false);

	const monthsToQuery = useMemo(() => {
		const today = dayjs();
		const maxDate = today.add(maxCoachActiveSlotDays, 'day');
		const months: { year: number; month: number }[] = [];
		let current = today.startOf('month');

		while (current.isBefore(maxDate) || current.isSame(maxDate, 'month')) {
			months.push({ year: current.year(), month: current.month() + 1 });
			current = current.add(1, 'month').startOf('month');
		}

		return months;
	}, [maxCoachActiveSlotDays]);

	const recentStudents: ExpertStudentResponse[] = [];
	const { AppointmentControllerApi, CalendarControllerApi, StudentStatisticControllerApi } = useApi();
	const {
		data: studentsInfiniteData,
		isLoading: loadingAllStudents,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useInfiniteQueryProvider(
		[QUERY_KEYS.findStudentStatisticsOfLoggedInExpert, debouncedSearch, liveServiceType],
		({ pageParam = 0 }) => StudentStatisticControllerApi.findStudentStatisticsOfLoggedInExpert(debouncedSearch, liveServiceType, pageParam as number, 20),
		{
			getNextPageParam: lastPage => {
				if (!lastPage.data?.data || lastPage.data.data.last) return undefined;
				return (lastPage.data.data.number || 0) + 1;
			},
			select: data => ({
				pages: data.pages.map(page => page.data?.data),
				pageParams: data.pageParams
			})
		}
	);

	const { data: topicsData } = useQueryProvider([QUERY_KEYS.findAppointmentTopics], () => AppointmentControllerApi.findAppointmentTopics(), {
		select: ({ data }) => data?.data
	});

	const { data: restrictData } = useQueryProvider(
		[QUERY_KEYS.findAppointmentRestrictionOfStudent, selectedStudent?.studentId?.toString() || ''],
		() => AppointmentControllerApi.findAppointmentRestrictionOfStudent(selectedStudent?.studentId as number),
		{
			select: ({ data }) => data?.data,
			enabled: !!selectedStudent?.studentId
		}
	);

	const { data: lessons } = useQueryProvider(
		[QUERY_KEYS.findLessonAppointmentSubjects, AppointmentSubjectResponseSubjectTypeEnum.Lesson, selectedStudent?.studentId?.toString() || ''],
		() => AppointmentControllerApi.findLessonAppointmentSubjects(selectedStudent?.studentId as number),
		{
			select: ({ data }) =>
				data?.data?.map(x => ({
					label: x.description,
					value: x.id?.toString()
				})) || [],
			enabled: !!selectedStudent?.studentId
		}
	);

	const { data: units } = useQueryProvider(
		[QUERY_KEYS.findAppointmentSubjects, AppointmentSubjectResponseSubjectTypeEnum.Unit, selectedStudent?.studentId?.toString() || '', liveServiceType, selectedLessonSubject],
		() => AppointmentControllerApi.findAppointmentSubjects(AppointmentSubjectResponseSubjectTypeEnum.Unit, liveServiceType, Number(selectedLessonSubject)),
		{
			select: ({ data }) =>
				data?.data?.map(x => ({
					label: x.description,
					value: x.id?.toString()
				})) || [],
			enabled: !!selectedLessonSubject
		}
	);

	const { data: subjects } = useQueryProvider(
		[QUERY_KEYS.findAppointmentSubjects, AppointmentSubjectResponseSubjectTypeEnum.Unit, selectedStudent?.studentId?.toString() || '', liveServiceType, selectedUnit],
		() => AppointmentControllerApi.findAppointmentSubjects(AppointmentSubjectResponseSubjectTypeEnum.Subject, liveServiceType, Number(selectedUnit)),
		{
			select: ({ data }) =>
				data?.data?.map(x => ({
					label: x.description,
					value: x.id?.toString()
				})) || [],
			enabled: !!selectedUnit
		}
	);

	const { data: timeSlotsData } = useQueryProvider(
		[QUERY_KEYS.findAvailableExpertCalendarSlotsOfDay, selectedDate, (selectedStudent?.studentId || '').toString()],
		() => CalendarControllerApi.findAvailableExpertCalendarSlotsOfDay(selectedStudent?.studentId as number, selectedDate, liveServiceType),
		{
			select: ({ data }) => data?.data?.filter(slot => slot.slotStatus === ExpertDailySlotResponseSlotStatusEnum.Available),
			refetchOnWindowFocus: true,
			enabled: !!(selectedStudent?.studentId && selectedDate)
		}
	);

	const month1 = monthsToQuery[0];
	const month2 = monthsToQuery[1];

	const { data: month1Data } = useQueryProvider(
		[QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, month1?.year.toString(), month1?.month.toString()],
		() => CalendarControllerApi.countExpertCalendarSlotAppointmentBetweenDates(month1.year, month1.month),
		{
			select: ({ data }) => data?.data || [],
			enabled: !!month1
		}
	);

	const { data: month2Data } = useQueryProvider(
		[QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, month2?.year.toString(), month2?.month.toString()],
		() => CalendarControllerApi.countExpertCalendarSlotAppointmentBetweenDates(month2.year, month2.month),
		{
			select: ({ data }) => data?.data || [],
			enabled: !!month2
		}
	);

	const calendarDaysData = useMemo(() => {
		return [...(month1Data || []), ...(month2Data || [])];
	}, [month1Data, month2Data]);

	const allStudents = useMemo(() => {
		return studentsInfiniteData?.pages?.flatMap(page => page?.content || []) || [];
	}, [studentsInfiniteData]);

	const saveAppointment = useMutationProvider((data: AppointmentCreateRequest) => AppointmentControllerApi.createAppointment(data), {
		onSuccess: () => {
			toast.success('Randevu başarıyla oluşturuldu');
		},
		onError: ExceptionManager.errorHandler
	});

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
		if (e.target.value === '') {
			setDebouncedSearch('');
		}
	};

	const handleSearchSubmit = () => {
		setDebouncedSearch(search);
	};

	const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleSearchSubmit();
		}
	};

	const calendarDays: typeof CALENDAR_DAYS = useMemo(() => {
		const today = dayjs();
		const maxDate = today.add(maxCoachActiveSlotDays, 'day');

		const restrictDate = isActiveRestriction ? (restrictData?.nextAvailableAppointmentDate ? dayjs(restrictData.nextAvailableAppointmentDate) : today) : today;

		const startDate = restrictDate.isAfter(today) ? restrictDate : today;

		const daysCount = maxDate.diff(startDate, 'day');

		const generatedDays = Array.from({ length: daysCount }, (_, index) => {
			const currentDate = startDate.add(index, 'day');

			return {
				weekday: currentDate.format('ddd'),
				date: currentDate.format('D'),
				slotDate: currentDate.format('YYYY-MM-DD'),
				slotCount: 0,
				appointmentCount: 0,
				isActive: true
			};
		});

		return generatedDays.map(day => {
			const dayData = calendarDaysData?.find(data => data?.slotDate === day?.slotDate);
			day.slotCount = dayData?.slotCount || 0;
			day.appointmentCount = dayData?.appointmentCount || 0;
			day.isActive = day.slotCount > 0 && day.slotCount > day.appointmentCount;
			return day;
		});
	}, [calendarDaysData, restrictData, maxCoachActiveSlotDays]);

	const canNavigatePrev = useMemo(() => {
		if (!calendarDays.length) return false;

		if (selectedDate) {
			const currentDateIndex = calendarDays.findIndex(day => day.slotDate === selectedDate);
			if (currentDateIndex <= 0) return false;

			for (let i = currentDateIndex - 1; i >= 0; i--) {
				if (calendarDays[i].isActive) {
					return true;
				}
			}
		}

		return false;
	}, [selectedDate, calendarDays]);

	const canNavigateNext = useMemo(() => {
		if (!calendarDays.length) return false;

		if (selectedDate) {
			const currentDateIndex = calendarDays.findIndex(day => day.slotDate === selectedDate);
			if (currentDateIndex >= calendarDays.length - 1) return false;

			for (let i = currentDateIndex + 1; i < calendarDays.length; i++) {
				if (calendarDays[i].isActive) {
					return true;
				}
			}
		}

		return false;
	}, [selectedDate, calendarDays]);

	const handleNext = () => {
		swiper?.slideNext();
	};

	const handlePrev = () => {
		swiper?.slidePrev();
	};

	const handleStudentSelect = (student: ExpertStudentResponse) => {
		setSelectedStudent(student);
		setSelectedLessonSubject('');
		setSelectedUnit('');
		setSelectedSubject('');
		setSelectedTopic(null);
		swiper?.slideNext();
	};

	const handleTopicSelect = (topic: AppointmentSubjectResponse) => {
		setSelectedTopic(topic);
	};

	const handleTimeSelect = (timeSlot: ExpertDailySlotResponse) => {
		setSelectedTimeSlot(timeSlot);
	};

	const handleDaySelect = (selectedDay: CalendarDay) => {
		setSelectedDate(selectedDay.slotDate);
		setSelectedTimeSlot(null);
	};

	const formatDate = (date: string) => {
		return dayjs(date).format('D MMMM YYYY');
	};

	const handleLessonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedLessonSubject(e.target.value);
		setSelectedUnit('');
		setSelectedSubject('');
		setSelectedTopic(null);
	};

	const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedUnit(e.target.value);
		setSelectedSubject('');
		setSelectedTopic(null);
	};

	const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSelectedSubject(e.target.value);

		if (e.target.value) {
			const subjectTopic: AppointmentSubjectResponse = {
				id: parseInt(e.target.value),
				description: subjects?.find(s => s.value === e.target.value)?.label || ''
			};
			setSelectedTopic(subjectTopic);
		}
	};

	const resetStates = () => {
		setSearch('');
		setDebouncedSearch('');
		setSelectedStudent(null);
		setSelectedTopic(null);
		setSelectedLessonSubject('');
		setSelectedUnit('');
		setSelectedSubject('');
		setSelectedDate('');
		setSelectedTimeSlot(null);
		if (swiper) {
			swiper.slideTo(0);
		}
	};

	const resetAndClose = () => {
		resetStates();
		const AppointmentDate = dayjs(selectedDate).format('YYYY-MM-DD');
		const date = dayjs(selectedDate).format('YYYY-MM');
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.getAppointmentCancellationCount, date]
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.countExpertCalendarSlotAppointmentBetweenDates, date]
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CREATED']
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAppointmentsOfTheDay, AppointmentDate, 'CANCELLED']
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAvailableExpertDailySlots, new Date(selectedDate)]
		});
		queryClient.invalidateQueries({
			queryKey: [QUERY_KEYS.findAvailableExpertCalendarSlotsOfDay]
		});
		onClose();
	};

	const scrollToSelectedDate = (dateToScroll: string) => {
		if (!daysGridRef.current) return;

		const selectedDayIndex = calendarDays.findIndex(day => day.slotDate === dateToScroll);

		if (selectedDayIndex === -1) return;

		const dayCells = daysGridRef.current.querySelectorAll('.day-cell');
		const selectedDayCell = dayCells[selectedDayIndex] as HTMLElement;

		if (selectedDayCell && daysGridRef.current.parentElement) {
			const container = daysGridRef.current.parentElement;
			const cellLeft = selectedDayCell.offsetLeft;
			const cellWidth = selectedDayCell.offsetWidth;
			const containerWidth = container.offsetWidth;

			const scrollLeft = cellLeft - containerWidth / 2 + cellWidth / 2;

			container.scrollTo({
				left: scrollLeft,
				behavior: 'smooth'
			});
		}
	};

	const handleCreateAppointment = async () => {
		if (selectedStudent && selectedTopic && selectedTimeSlot && selectedTimeSlot?.id && selectedTimeSlot?.slotType && selectedDate) {
			const data: AppointmentCreateRequest = {
				studentId: selectedStudent?.studentId as unknown as number,
				expertCalendarSlotId: selectedTimeSlot?.id,
				expertCalendarSlotType: selectedTimeSlot?.slotType,
				topicId: selectedTopic?.id as unknown as number,
				appointmentDate: selectedDate,
				liveServiceType: liveServiceType,
				branchId: selectedLessonSubject ? parseInt(selectedLessonSubject) : undefined
			};
			await saveAppointment.mutateAsync(data).finally(() => {
				resetAndClose();
			});
		}
	};

	const handlePrevDay = () => {
		if (!canNavigatePrev) return;

		const currentDateIndex = calendarDays.findIndex(day => day.slotDate === selectedDate);

		for (let i = currentDateIndex - 1; i >= 0; i--) {
			if (calendarDays[i].isActive) {
				const prevDay = calendarDays[i];
				setSelectedDate(prevDay.slotDate);
				setCurrentDate(dayjs(prevDay.slotDate));
				scrollToSelectedDate(prevDay.slotDate);
				break;
			}
		}
	};

	const handleNextDay = () => {
		if (!canNavigateNext) return;

		const currentDateIndex = calendarDays.findIndex(day => day.slotDate === selectedDate);

		for (let i = currentDateIndex + 1; i < calendarDays.length; i++) {
			if (calendarDays[i].isActive) {
				const nextDay = calendarDays[i];
				setSelectedDate(nextDay.slotDate);
				setCurrentDate(dayjs(nextDay.slotDate));
				scrollToSelectedDate(nextDay.slotDate);
				break;
			}
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
				resetAndClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	useEffect(() => {
		if (restrictData?.nextAvailableAppointmentDate && calendarDays.length > 0) {
			const today = dayjs();
			const restrictDate = isActiveRestriction ? (restrictData?.nextAvailableAppointmentDate ? dayjs(restrictData.nextAvailableAppointmentDate) : today) : today;
			const startDate = restrictDate.isAfter(today) ? restrictDate : today;

			setSelectedDate(startDate.format('YYYY-MM-DD'));
			setCurrentDate(startDate);
		}
	}, [restrictData, calendarDays.length]);

	useEffect(() => {
		if (!studentsListRef.current) return;

		const options = {
			root: studentsListRef.current,
			rootMargin: '100px',
			threshold: 0.1
		};

		const observer = new IntersectionObserver(entries => {
			const [entry] = entries;
			if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		}, options);

		const sentinel = document.createElement('div');
		sentinel.className = 'load-more-sentinel';
		sentinel.style.height = '20px';
		sentinel.style.width = '100%';

		studentsListRef.current.appendChild(sentinel);
		observer.observe(sentinel);

		return () => {
			if (sentinel.parentNode) {
				sentinel.parentNode.removeChild(sentinel);
			}
			observer.disconnect();
		};
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	return (
		<div
			className="appointment-container"
			ref={modalRef}>
			<Swiper
				onSwiper={setSwiper}
				allowTouchMove={false}
				preventClicks={false}
				simulateTouch={true}
				touchStartPreventDefault={false}
				preventClicksPropagation={false}
				className="appointment-swiper swiper-no-swiping">
				<SwiperSlide>
					<div className="slide-content">
						<div className="title-wrapper">
							<h2>Randevu Oluştur</h2>
						</div>

						<div className="search-container">
							<div className="search-wrapper">
								<input
									type="text"
									placeholder="Öğrenci ara"
									value={search}
									onChange={handleSearchChange}
									onKeyUp={handleKeyPress}
								/>
								<button
									className="appoinment-search-button"
									onClick={handleSearchSubmit}
									aria-label="Ara">
									<i className="icons-minimalistic-magnifer" />
								</button>
							</div>
						</div>

						<div
							className="scrollable-content"
							ref={studentsListRef}>
							{recentStudents?.length > 0 && (
								<>
									<h3>Son Görüştüklerim</h3>
									<div className="students-list recent-meetings">
										{recentStudents?.map(student => (
											<div
												key={student.studentId}
												className="student-card">
												<img
													src={student?.avatarUrl}
													alt={`${student.studentName} ${student.studentSurname}`}
												/>
												<div className="student-info">
													<p className="name">
														{student.studentName} {student.studentSurname}
													</p>
													<p className="class">{student.studentPreferredGradeName}</p>
												</div>
												{/*WAITING FOR API*/}
												{/*{student?.lastMeeting && (*/}
												{/*  <div className="meeting-status">*/}
												{/*    <span>*/}
												{/*      {student.lastMeeting.date === "2024-01-20"*/}
												{/*        ? "Bugün"*/}
												{/*        : "Dün"}*/}
												{/*      , {student.lastMeeting.time}*/}
												{/*    </span>*/}
												{/*  </div>*/}
												{/*)}*/}
											</div>
										))}
									</div>
								</>
							)}
							<h3>Öğrencilerim ({studentsInfiniteData?.pages?.[0]?.totalElements || 0})</h3>

							<div className="students-list all-students">
								<ReactPlaceholder
									ready={!loadingAllStudents}
									style={{ width: '90%', marginInline: 'auto' }}
									showLoadingAnimation={true}
									rows={20}
									type="text">
									{allStudents?.map(student => (
										<div
											key={student.studentId}
											className="student-card"
											onClick={() => handleStudentSelect(student)}>
											<img
												src={student?.avatarUrl}
												alt={`${student?.studentName} ${student?.studentSurname}`}
											/>
											<div className="student-info">
												<p className="name">
													{student?.studentName} {student?.studentSurname}
												</p>
												<p className="class">{student?.preferredGradeName}</p>
											</div>
											<div className="select-button">
												<p className="select-button-text">Seç</p>
												<i className="icons-arrow-right" />
											</div>
										</div>
									))}
									{isFetchingNextPage && (
										<div className="loading-more">
											<ReactPlaceholder
												ready={false}
												showLoadingAnimation={true}
												rows={3}
												type="text">
												<></>
											</ReactPlaceholder>
										</div>
									)}
								</ReactPlaceholder>
							</div>
						</div>
					</div>
				</SwiperSlide>

				<SwiperSlide>
					<div className="slide-content">
						{liveServiceType !== ManualCalendarSlotUpdateRequestLiveServiceTypeEnum.OneOnOneLecture ? (
							<>
								<div className="title-wrapper">
									<i
										className="icons-arrow-left"
										onClick={handlePrev}
									/>
									<h2>Görüşme Konusunu Seç</h2>
								</div>
								<div className="topics-list">
									{topicsData?.map(topic => (
										<div
											key={topic.id}
											className={clsx('topic-card', {
												selected: selectedTopic?.id === topic.id
											})}
											onClick={() => handleTopicSelect(topic)}>
											<div className="radio">
												<div className="radio-inner" />
											</div>
											<span>{topic.description}</span>
										</div>
									))}
								</div>
							</>
						) : (
							<>
								<div className="title-wrapper">
									<i
										className="icons-arrow-left"
										onClick={handlePrev}
									/>
									<h2>Ünite ve Konu Seç</h2>
								</div>
								<div className="subject-select-wrapper first-select">
									<Select
										options={lessons as unknown as SelectOption[]}
										value={selectedLessonSubject}
										variant="default"
										placeholder="Ders Seçiniz"
										onChange={handleLessonChange}
										className="subject-select"
									/>
								</div>
								<div className="subject-select-wrapper">
									<Select
										options={units as unknown as SelectOption[]}
										value={selectedUnit}
										variant="default"
										placeholder="Ünite Seçiniz"
										onChange={handleUnitChange}
										className="subject-select"
										disabled={!selectedLessonSubject}
									/>
								</div>
								<div className="subject-select-wrapper">
									<Select
										options={subjects as unknown as SelectOption[]}
										value={selectedSubject}
										variant="default"
										placeholder="Konu Seçiniz"
										onChange={handleSubjectChange}
										className="subject-select"
										disabled={!selectedUnit}
									/>
								</div>
							</>
						)}

						<div className="button-group">
							<div
								className={`next-button ${!selectedTopic ? 'disabled' : ''}`}
								onClick={() => selectedTopic && handleNext()}>
								<span className="button-text">Devam Et</span>
								<i className="icons-arrow-right" />
							</div>
						</div>
					</div>
				</SwiperSlide>

				<SwiperSlide>
					<div className="slide-content">
						<div className="title-wrapper">
							<i
								className="icons-arrow-left"
								onClick={handlePrev}
							/>
							<h2>Randevu Tarihini Belirle</h2>
						</div>

						<div className="calendar-section">
							<div className="calendar-header">
								<div className="month-display">{dayjs(selectedDate).format('MMMM YYYY')}</div>
								<div className="nav-buttons">
									<i
										className={`icons-alt-arrow-left ${!canNavigatePrev ? 'disabled' : ''}`}
										onClick={handlePrevDay}
									/>
									<i
										className={`icons-alt-arrow-right ${!canNavigateNext ? 'disabled' : ''}`}
										onClick={handleNextDay}
									/>
								</div>
							</div>

							<div className="days-grid-wrapper">
								<div
									className="days-grid"
									ref={daysGridRef}>
									{calendarDays?.map((day, index) => (
										<div
											key={index}
											className={`day-cell ${selectedDate === day.slotDate ? 'selected' : ''} ${
												day.isActive ? 'isActive' : ''
											} ${day.slotDate === currentDate.format('YYYY-MM-DD') ? 'today' : ''}`}
											onClick={() => day.isActive && handleDaySelect(day)}>
											<span className="date">{day.date}</span>
											<span className="weekday">{day.weekday}</span>
											{day.slotCount === 0 ? (
												<i className="icons-calendar-add" />
											) : (
												<span className={`slot-info ${day.appointmentCount === 0 ? 'unavailable' : ''}`}>{`${day.appointmentCount}/${day.slotCount}`}</span>
											)}
										</div>
									))}
								</div>
							</div>

							<div className="available-times">
								<h3>Görüşmeye müsait saatlerim</h3>
								<div className="time-grid">
									{timeSlotsData?.map(timeSlot => (
										<div
											key={timeSlot?.slot?.id}
											className={`time-slot ${selectedTimeSlot?.slot?.slotStartTime === timeSlot?.slot?.slotStartTime ? 'selected' : ''} ${!selectedDate ? 'disabled' : ''}`}
											onClick={() => selectedDate && handleTimeSelect(timeSlot)}>
											<div className="time-slot-content">
												<div className="radio">
													<div className="radio-inner" />
												</div>
												<span> {dayjs(timeSlot?.slot?.slotStartTime as unknown as string, 'HH:mm').format('HH:mm')}</span>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						<div className="button-group">
							<div
								className={`next-button ${!selectedDate || !selectedTimeSlot ? 'disabled' : ''}`}
								onClick={() => selectedDate && selectedTimeSlot && handleNext()}>
								<span className="button-text">Tarihi Onayla</span>
								<i className="icons-arrow-right" />
							</div>
						</div>
					</div>
				</SwiperSlide>

				<SwiperSlide>
					<div className="slide-content">
						<div className="confirmation">
							<div className="appointment-confirmation-header">
								<i className="icons-calendar-mark" />
								<h2>Randevuyu Onayla</h2>
								<p>Randevu bilgileri öğrencinize mesaj ve bildirim olarak gönderilecek! Şimdiden iyi görüşmeler!</p>
							</div>
							<div className="appointment-details">
								<div className="student-info">
									<img
										src={selectedStudent?.avatarUrl}
										alt={`${selectedStudent?.studentName} ${selectedStudent?.studentSurname}`}
									/>
									<div>
										<h3>
											{selectedStudent?.studentName} {selectedStudent?.studentSurname}
										</h3>
										<span>{selectedStudent?.studentPreferredGradeName}</span>
									</div>
								</div>

								<div className="date-time">
									<i className="icons-calendar-mark" />
									<div>
										<div className="date">{formatDate(selectedDate)}</div>
										<div className="time">{dayjs(selectedTimeSlot?.slot?.slotStartTime as unknown as string, 'HH:mm').format('HH:mm')}</div>
									</div>
								</div>
							</div>

							<div className="button-group">
								<button onClick={handlePrev}>Düzenle</button>
								<button
									className="create-button"
									onClick={handleCreateAppointment}
									disabled={saveAppointment?.isLoading}>
									Randevuyu Oluştur
								</button>
							</div>
						</div>
					</div>
				</SwiperSlide>
			</Swiper>
		</div>
	);
};

export default AppointmentModal;
